import { c as _c } from "react/compiler-runtime";
import { IconAsterisk, IconBan, IconChevronRight, IconCircleCheck, IconCircleHalf2, IconClockPause, IconDotsVertical, IconExternalLink, IconFilter, IconHelp, IconWaveSquare, IconX } from "@tabler/icons-react";
import { AnimatePresence, useReducedMotion } from "motion/react";
import * as m from "motion/react-m";
import { useCallback, useMemo, useState, useTransition } from "react";
import { PillCount } from "@/components/domain/pill-count";
import type { SeoResponse } from "@domainstack/types";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@domainstack/ui/accordion";
import { Button } from "@domainstack/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@domainstack/ui/empty";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@domainstack/ui/input-group";
import { ToggleGroup, ToggleGroupItem } from "@domainstack/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";
import { cn } from "@domainstack/ui/utils";
type RobotsRule = {
  type: "allow" | "disallow" | "crawlDelay" | "contentSignal";
  value: string;
};
function getRuleItems(rules: RobotsRule[], scope: string) {
  const seen = new Map<string, number>();
  return rules.map(rule => {
    const baseKey = `${scope}-${rule.type}-${rule.value || "empty"}`;
    const duplicateCount = seen.get(baseKey) ?? 0;
    seen.set(baseKey, duplicateCount + 1);
    return {
      key: `${baseKey}-${duplicateCount}`,
      rule
    };
  });
}
function highlight(text: string, q: string) {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + q.length);
  const after = text.slice(idx + q.length);
  return <>
      {before}
      <span className="bg-yellow-500/30 dark:bg-yellow-400/30">{match}</span>
      {after}
    </>;
}
function useProgressiveReveal(items, initialVisible) {
  const $ = _c(14);
  const [visible, setVisible] = useState(initialVisible);
  const total = items.length;
  const more = total - visible;
  const [prevVisible, setPrevVisible] = useState(initialVisible);
  const [seenVisible, setSeenVisible] = useState(initialVisible);
  if (visible !== seenVisible) {
    setPrevVisible(seenVisible);
    setSeenVisible(visible);
  }
  const prev = Math.min(prevVisible, visible, total);
  let t0;
  if ($[0] !== items || $[1] !== prev) {
    t0 = items.slice(0, prev);
    $[0] = items;
    $[1] = prev;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const existing = t0;
  let t1;
  if ($[3] !== items || $[4] !== prev || $[5] !== total || $[6] !== visible) {
    t1 = items.slice(prev, Math.min(visible, total));
    $[3] = items;
    $[4] = prev;
    $[5] = total;
    $[6] = visible;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const added = t1;
  let t2;
  if ($[8] !== added || $[9] !== existing || $[10] !== more || $[11] !== total || $[12] !== visible) {
    t2 = {
      existing,
      added,
      more,
      total,
      visible,
      setVisible
    };
    $[8] = added;
    $[9] = existing;
    $[10] = more;
    $[11] = total;
    $[12] = visible;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2 as const;
}
export function RobotsSummary(t0) {
  const $ = _c(52);
  const {
    domain,
    robots
  } = t0;
  const isNonEmpty = _temp;
  let t1;
  if ($[0] !== robots?.groups) {
    t1 = robots?.groups.reduce((acc, g) => acc + g.rules.filter(r_0 => r_0.type === "disallow" && isNonEmpty(r_0)).length, 0) ?? 0;
    $[0] = robots?.groups;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const disallows = t1;
  let t2;
  if ($[2] !== robots?.groups) {
    t2 = robots?.groups.reduce((acc_0, g_0) => acc_0 + g_0.rules.filter(r_1 => r_1.type === "allow" && isNonEmpty(r_1)).length, 0) ?? 0;
    $[2] = robots?.groups;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const allows = t2;
  let t3;
  if ($[4] !== allows || $[5] !== disallows) {
    t3 = {
      allows,
      disallows
    };
    $[4] = allows;
    $[5] = disallows;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const counts = t3;
  let t4;
  bb0: {
    const groups = robots?.groups ?? [];
    for (const g_1 of groups) {
      for (const r_2 of g_1.rules) {
        if ((r_2.type === "allow" || r_2.type === "disallow") && r_2.value.trim() !== "") {
          t4 = true;
          break bb0;
        }
      }
    }
    t4 = false;
  }
  const hasAnyListedRules = t4;
  let t5;
  bb1: {
    const groups_0 = robots?.groups ?? [];
    for (const g_2 of groups_0) {
      for (const r_3 of g_2.rules) {
        if ((r_3.type === "allow" || r_3.type === "disallow") && r_3.value.trim() === "") {
          t5 = true;
          break bb1;
        }
      }
    }
    t5 = false;
  }
  const hasEmptyRulesGroups = t5;
  const [query, setQuery] = useState("");
  const [only, setOnly] = useState("all");
  const [, startTransition] = useTransition();
  const rankAgents = _temp2;
  let t6;
  if ($[7] !== only || $[8] !== query || $[9] !== robots?.groups) {
    const base = robots?.groups?.slice() ?? [];
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = (a, b) => rankAgents(a.userAgents) - rankAgents(b.userAgents);
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    const sorted = base.sort(t7);
    const isNonEmpty_0 = _temp3;
    let t8;
    if ($[12] !== only || $[13] !== query) {
      t8 = g_3 => {
        const hasEmptyAllow = g_3.rules.some(r_5 => r_5.type === "allow" && !isNonEmpty_0(r_5));
        const hasEmptyDisallow = g_3.rules.some(r_6 => r_6.type === "disallow" && !isNonEmpty_0(r_6));
        const queryLower = query?.toLowerCase();
        const visible = g_3.rules.filter(r_7 => isNonEmpty_0(r_7) && (only === "all" || r_7.type === only) && (!queryLower || r_7.value.toLowerCase().includes(queryLower)));
        return {
          userAgents: g_3.userAgents,
          rules: visible,
          hasEmptyAllow,
          hasEmptyDisallow
        };
      };
      $[12] = only;
      $[13] = query;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    t6 = sorted.map(t8);
    $[7] = only;
    $[8] = query;
    $[9] = robots?.groups;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const filteredGroups = t6;
  let t7;
  if ($[15] !== filteredGroups) {
    t7 = filteredGroups.some(_temp4);
    $[15] = filteredGroups;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const hasFilteredRules = t7;
  let t8;
  if ($[17] !== only || $[18] !== query) {
    t8 = query.trim().length > 0 || only !== "all";
    $[17] = only;
    $[18] = query;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const filtersActive = t8;
  let t9;
  if ($[20] !== filteredGroups || $[21] !== filtersActive) {
    t9 = filtersActive ? filteredGroups.filter(_temp5) : filteredGroups;
    $[20] = filteredGroups;
    $[21] = filtersActive;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const displayGroups = t9;
  const t10 = `https://${domain}/robots.txt`;
  let t11;
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span>robots.txt</span>;
    t12 = <IconExternalLink className="relative bottom-px inline-flex size-3" aria-hidden={true} />;
    $[23] = t11;
    $[24] = t12;
  } else {
    t11 = $[23];
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t10) {
    t13 = <a href={t10} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline hover:underline-offset-3">{t11}{t12}</a>;
    $[25] = t10;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const t14 = counts.allows + counts.disallows;
  let t15;
  if ($[27] !== t14) {
    t15 = <PillCount count={t14} color="blue" />;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t13 || $[30] !== t15) {
    t16 = <div className="mt-5 flex items-center gap-2 text-[11px] leading-none tracking-[0.08em] text-foreground/70 uppercase dark:text-foreground/80">{t13}{t15}</div>;
    $[29] = t13;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== counts.allows || $[33] !== counts.disallows || $[34] !== displayGroups || $[35] !== filtersActive || $[36] !== hasAnyListedRules || $[37] !== hasEmptyRulesGroups || $[38] !== hasFilteredRules || $[39] !== only || $[40] !== query || $[41] !== robots?.sitemaps?.length || $[42] !== startTransition) {
    t17 = hasAnyListedRules ? <><div className="flex flex-col gap-2 sm:flex-row sm:items-center"><InputGroup className="sm:flex-1"><InputGroupInput name="robots-filter" placeholder={"Filter rules\u2026"} value={query} onChange={e => startTransition(() => setQuery(e.currentTarget.value))} aria-label="Filter robots rules" autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false} /><InputGroupAddon><IconFilter aria-hidden={true} /></InputGroupAddon>{query ? <InputGroupAddon align="inline-end"><InputGroupButton size="sm" variant="ghost" onClick={() => setQuery("")} aria-label="Clear filter"><IconX aria-hidden={true} /></InputGroupButton></InputGroupAddon> : null}</InputGroup><ToggleGroup multiple={false} value={[only]} onValueChange={groupValue => {
          const next = groupValue[0] as typeof only | undefined;
          startTransition(() => setOnly(next ?? "all"));
        }} spacing={1} className="relative h-9 w-full items-stretch overflow-hidden rounded-lg border border-black/8 bg-muted/50 p-1 text-muted-foreground sm:w-auto dark:border-white/10 [&>*]:flex-1 sm:[&>*]:flex-none"><ToggleGroupItem value="all" className="h-full"><IconCircleHalf2 className="size-3.5 text-accent-blue" aria-hidden={true} /><span className="text-[13px]">All</span><PillCount count={counts.allows + counts.disallows} color="slate" /></ToggleGroupItem><ToggleGroupItem value="allow" className="h-full"><IconCircleCheck className="size-3.5 text-accent-green" aria-hidden={true} /><span className="text-[13px]">Allow</span><PillCount count={counts.allows} color="slate" /></ToggleGroupItem><ToggleGroupItem value="disallow" className="h-full"><IconBan className="size-3.5 text-destructive" aria-hidden={true} /><span className="text-[13px]">Disallow</span><PillCount count={counts.disallows} color="slate" /></ToggleGroupItem></ToggleGroup></div>{filtersActive && !hasFilteredRules ? <div className="text-sm text-muted-foreground">No matching rules.<Button variant="link" className="px-1" onClick={() => {
          startTransition(() => {
            setQuery("");
            setOnly("all");
          });
        }}>Reset filters</Button></div> : null}<GroupsAccordion groups={displayGroups} query={query} only={only} /></> : hasEmptyRulesGroups ? <GroupsAccordion groups={displayGroups} query={query} only={only} /> : robots?.sitemaps?.length ? <Empty className="border border-dashed"><EmptyHeader><EmptyMedia variant="icon"><IconHelp aria-hidden={true} /></EmptyMedia><EmptyTitle>No Crawl Rules Detected</EmptyTitle><EmptyDescription>This website’s robots.txt only declares sitemaps; no crawl rules are specified.</EmptyDescription></EmptyHeader></Empty> : null;
    $[32] = counts.allows;
    $[33] = counts.disallows;
    $[34] = displayGroups;
    $[35] = filtersActive;
    $[36] = hasAnyListedRules;
    $[37] = hasEmptyRulesGroups;
    $[38] = hasFilteredRules;
    $[39] = only;
    $[40] = query;
    $[41] = robots?.sitemaps?.length;
    $[42] = startTransition;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== robots) {
    t18 = robots?.sitemaps?.length ? <SitemapsList items={robots.sitemaps} /> : null;
    $[44] = robots;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t17 || $[47] !== t18) {
    t19 = <div className="space-y-4">{t17}{t18}</div>;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t16 || $[50] !== t19) {
    t20 = <div className="space-y-4 rounded-xl">{t16}{t19}</div>;
    $[49] = t16;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  return t20;
}
function _temp5(g_5) {
  return g_5.rules.length > 0;
}
function _temp4(g_4) {
  return g_4.rules.length > 0;
}
function _temp3(r_4) {
  return r_4.value.trim() !== "";
}
function _temp2(agents) {
  const joined = agents.join(",").toLowerCase();
  if (agents.includes("*")) {
    return 0;
  }
  if (/googlebot/.test(joined)) {
    return 1;
  }
  return 2;
}
function _temp(r) {
  return r.value.trim() !== "";
}
function displayUserAgents(agents: string[]): string[] {
  if (!agents.includes("*")) return agents;
  return ["*", ...agents.filter(ua => ua !== "*")];
}
function RobotsGroupHeader(t0) {
  const $ = _c(18);
  const {
    userAgents,
    allowN,
    disallowN,
    showAllow: t1,
    showDisallow: t2
  } = t0;
  const showAllow = t1 === undefined ? true : t1;
  const showDisallow = t2 === undefined ? true : t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== userAgents) {
    const agents = displayUserAgents(userAgents);
    t6 = "flex w-full items-start justify-between gap-3";
    t3 = "flex min-w-0 flex-wrap items-center gap-1.5";
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <IconChevronRight className="size-3 text-muted-foreground transition-transform group-data-[panel-open]/accordion:rotate-90 motion-reduce:transition-none" aria-hidden={true} />;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    t5 = agents.map(_temp6);
    $[0] = userAgents;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
  } else {
    t3 = $[1];
    t4 = $[2];
    t5 = $[3];
    t6 = $[4];
  }
  let t7;
  if ($[6] !== t3 || $[7] !== t4 || $[8] !== t5) {
    t7 = <div className={t3}>{t4}{t5}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = showAllow ? `${allowN} allow` : null;
  const t9 = showAllow && showDisallow ? " \xB7 " : null;
  const t10 = showDisallow ? `${disallowN} disallow` : null;
  let t11;
  if ($[10] !== t10 || $[11] !== t8 || $[12] !== t9) {
    t11 = <div className="shrink-0 pt-1 text-xs leading-none whitespace-nowrap text-muted-foreground">{t8}{t9}{t10}</div>;
    $[10] = t10;
    $[11] = t8;
    $[12] = t9;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t11 || $[15] !== t6 || $[16] !== t7) {
    t12 = <div className={t6}>{t7}{t11}</div>;
    $[14] = t11;
    $[15] = t6;
    $[16] = t7;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  return t12;
}
function _temp6(ua) {
  return <span key={ua} className={cn("flex items-center gap-1 rounded px-1.5 py-1 text-xs leading-none whitespace-nowrap", ua === "*" ? "bg-accent-purple/18 text-accent-purple" : "bg-muted")}>{ua === "*" ? <><IconAsterisk className="size-3" aria-hidden={true} />All bots</> : ua}</span>;
}
function GroupsAccordion(t0) {
  const $ = _c(14);
  const {
    groups,
    query,
    only
  } = t0;
  const shouldReduceMotion = useReducedMotion();
  const defaultIdx = groups.findIndex(_temp7);
  const defaultValue = defaultIdx >= 0 ? `g-${defaultIdx}` : undefined;
  const isSearching = Boolean(query);
  let t1;
  if ($[0] !== groups || $[1] !== isSearching) {
    t1 = isSearching ? groups.map(_temp8) : undefined;
    $[0] = groups;
    $[1] = isSearching;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const openValues = t1;
  let t2;
  if ($[3] !== groups || $[4] !== isSearching || $[5] !== only || $[6] !== query || $[7] !== shouldReduceMotion) {
    t2 = () => <AnimatePresence initial={false}>{groups.map((g_0, idx_0) => {
        const allowN = g_0.rules.filter(_temp9).length;
        const disallowN = g_0.rules.filter(_temp10).length;
        const showAllow = isSearching ? true : only !== "disallow";
        const showDisallow = isSearching ? true : only !== "allow";
        const stableKey = g_0.userAgents.join(",");
        return <m.div key={stableKey} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: shouldReduceMotion ? 0 : 0.2,
          ease: "easeOut"
        }}><AccordionItem value={`g-${idx_0}`}><AccordionTrigger className="group/accordion items-start px-2 py-2 hover:bg-accent/35 hover:no-underline data-[panel-open]:pr-2 [&>svg]:hidden"><RobotsGroupHeader userAgents={g_0.userAgents} allowN={allowN} disallowN={disallowN} showAllow={showAllow} showDisallow={showDisallow} /></AccordionTrigger><AccordionContent className="pb-2"><GroupContent rules={g_0.rules} query={query} only={only} hasEmptyAllow={g_0.hasEmptyAllow} hasEmptyDisallow={g_0.hasEmptyDisallow} /></AccordionContent></AccordionItem></m.div>;
      })}</AnimatePresence>;
    $[3] = groups;
    $[4] = isSearching;
    $[5] = only;
    $[6] = query;
    $[7] = shouldReduceMotion;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const renderItems = t2;
  let t3;
  if ($[9] !== defaultValue || $[10] !== isSearching || $[11] !== openValues || $[12] !== renderItems) {
    t3 = isSearching ? <Accordion key="accordion-search" multiple={true} value={openValues}>{renderItems()}</Accordion> : <Accordion key={`accordion-default-${defaultValue}`} defaultValue={defaultValue ? [defaultValue] : []}>{renderItems()}</Accordion>;
    $[9] = defaultValue;
    $[10] = isSearching;
    $[11] = openValues;
    $[12] = renderItems;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
function _temp10(r_0) {
  return r_0.type === "disallow";
}
function _temp9(r) {
  return r.type === "allow";
}
function _temp8(_, idx) {
  return `g-${idx}`;
}
function _temp7(g) {
  return g.userAgents.includes("*");
}
function GroupContent(t0) {
  const $ = _c(40);
  const {
    rules,
    query,
    only,
    hasEmptyAllow,
    hasEmptyDisallow
  } = t0;
  const isSearching = query.trim().length > 0;
  const shouldReduceMotion = useReducedMotion();
  const {
    existing,
    added,
    more,
    total,
    visible,
    setVisible
  } = useProgressiveReveal(rules, 6);
  if (isSearching) {
    let t1;
    let t2;
    if ($[0] !== query || $[1] !== rules) {
      const ruleItems = getRuleItems(rules, "all");
      const firstRuleKey = ruleItems[0]?.key;
      t1 = "flex flex-col";
      t2 = ruleItems.map(t3 => {
        const {
          key,
          rule
        } = t3;
        return <RuleRow key={key} rule={rule} query={query} isFirst={key === firstRuleKey} />;
      });
      $[0] = query;
      $[1] = rules;
      $[2] = t1;
      $[3] = t2;
    } else {
      t1 = $[2];
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t1 || $[5] !== t2) {
      t3 = <div className={t1}>{t2}</div>;
      $[4] = t1;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[7] !== added || $[8] !== existing || $[9] !== hasEmptyAllow || $[10] !== hasEmptyDisallow || $[11] !== only || $[12] !== query || $[13] !== rules.length || $[14] !== shouldReduceMotion || $[15] !== visible) {
    const existingItems = getRuleItems(existing, "existing");
    const firstExistingKey = existingItems[0]?.key;
    const addedItems = getRuleItems(added, "added");
    t1 = "flex flex-col py-2";
    if ($[21] !== hasEmptyDisallow || $[22] !== only || $[23] !== rules.length) {
      t2 = rules.length === 0 && hasEmptyDisallow && only !== "allow" ? <div className="rounded-md bg-muted/30 px-2 py-1 text-[13px] text-muted-foreground/90">No disallow restrictions (allow all)</div> : null;
      $[21] = hasEmptyDisallow;
      $[22] = only;
      $[23] = rules.length;
      $[24] = t2;
    } else {
      t2 = $[24];
    }
    if ($[25] !== hasEmptyAllow || $[26] !== only || $[27] !== rules.length) {
      t3 = rules.length === 0 && hasEmptyAllow && only !== "disallow" ? <div className="rounded-md bg-muted/30 px-2 py-1 text-[13px] text-muted-foreground/90">No explicit allow paths</div> : null;
      $[25] = hasEmptyAllow;
      $[26] = only;
      $[27] = rules.length;
      $[28] = t3;
    } else {
      t3 = $[28];
    }
    t4 = existingItems.map(t6 => {
      const {
        key: key_0,
        rule: rule_0
      } = t6;
      return <RuleRow key={key_0} rule={rule_0} query={query} isFirst={key_0 === firstExistingKey} />;
    });
    t5 = added.length > 0 ? <m.div key={`added-${visible}`} initial={shouldReduceMotion ? false : {
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: shouldReduceMotion ? 0 : 0.2,
      ease: "easeOut"
    }} className="flex flex-col">{addedItems.map(t7 => {
        const {
          key: key_1,
          rule: rule_1
        } = t7;
        return <RuleRow key={key_1} rule={rule_1} query={query} />;
      })}</m.div> : null;
    $[7] = added;
    $[8] = existing;
    $[9] = hasEmptyAllow;
    $[10] = hasEmptyDisallow;
    $[11] = only;
    $[12] = query;
    $[13] = rules.length;
    $[14] = shouldReduceMotion;
    $[15] = visible;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t1 = $[16];
    t2 = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
  }
  let t6;
  if ($[29] !== more || $[30] !== setVisible || $[31] !== total) {
    t6 = more > 0 ? <div className="mt-1 flex justify-start"><Button size="sm" variant="outline" className="text-[12px]" onClick={() => setVisible(total)}><IconDotsVertical className="!size-3.5" aria-hidden={true} /><span>Show {more} more</span></Button></div> : null;
    $[29] = more;
    $[30] = setVisible;
    $[31] = total;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] !== t1 || $[34] !== t2 || $[35] !== t3 || $[36] !== t4 || $[37] !== t5 || $[38] !== t6) {
    t7 = <div className={t1}>{t2}{t3}{t4}{t5}{t6}</div>;
    $[33] = t1;
    $[34] = t2;
    $[35] = t3;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  return t7;
}
function RuleRow(t0) {
  const $ = _c(13);
  const {
    rule,
    query,
    isFirst: t1
  } = t0;
  const isFirst = t1 === undefined ? false : t1;
  const t2 = isFirst && "border-t-0";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("group flex items-center gap-2 border-t border-muted px-2 py-2.5 font-mono text-xs", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== rule.type) {
    t4 = <RuleTypeDot type={rule.type} />;
    $[2] = rule.type;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== query || $[5] !== rule.value) {
    t5 = highlight(rule.value, query);
    $[4] = query;
    $[5] = rule.value;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5) {
    t6 = <span className="truncate">{t5}</span>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t6) {
    t7 = <div className={t3}>{t4}{t6}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
const ruleTypeConfig = {
  allow: {
    Icon: IconCircleCheck,
    label: "Allow",
    colorClass: "text-accent-green"
  },
  disallow: {
    Icon: IconBan,
    label: "Disallow",
    colorClass: "text-destructive"
  },
  crawlDelay: {
    Icon: IconClockPause,
    label: "Crawl delay",
    colorClass: "text-accent-orange"
  },
  contentSignal: {
    Icon: IconWaveSquare,
    label: "Content signal",
    colorClass: "text-accent-purple"
  }
} as const;
function RuleTypeDot(t0) {
  const $ = _c(13);
  const {
    type
  } = t0;
  const {
    Icon,
    label,
    colorClass
  } = ruleTypeConfig[type];
  let t1;
  if ($[0] !== colorClass) {
    t1 = cn("size-3.5", colorClass);
    $[0] = colorClass;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== Icon || $[3] !== t1) {
    t2 = <Icon className={t1} aria-hidden={true} />;
    $[2] = Icon;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== label || $[6] !== t2) {
    t3 = <TooltipTrigger render={<button type="button" className="flex size-4 items-center justify-center" aria-label={label}>{t2}</button>} />;
    $[5] = label;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== label) {
    t4 = <TooltipContent side="left" sideOffset={8}>{label}</TooltipContent>;
    $[8] = label;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <Tooltip>{t3}{t4}</Tooltip>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function SitemapLink(t0) {
  const $ = _c(3);
  const {
    url
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <IconExternalLink className="size-3" aria-hidden={true} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== url) {
    t2 = <div className="flex items-center"><a className="flex items-center gap-1.5 truncate text-[13px] font-medium text-foreground/85 hover:text-foreground/60 hover:no-underline" href={url} target="_blank" rel="noopener">{url}{t1}</a></div>;
    $[1] = url;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function SitemapsList(t0) {
  const $ = _c(20);
  const {
    items
  } = t0;
  const shouldReduceMotion = useReducedMotion();
  const {
    existing,
    added,
    more,
    total,
    visible,
    setVisible
  } = useProgressiveReveal(items, 2);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span>Sitemaps</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== items.length) {
    t2 = <div className="flex items-center gap-2 text-[11px] leading-none tracking-[0.08em] text-foreground/70 uppercase dark:text-foreground/80">{t1}<PillCount count={items.length} color="green" /></div>;
    $[1] = items.length;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== existing) {
    t3 = existing.map(_temp11);
    $[3] = existing;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== added || $[6] !== shouldReduceMotion || $[7] !== visible) {
    t4 = added.length > 0 ? <m.div key={`sitemaps-added-${visible}`} initial={shouldReduceMotion ? false : {
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: shouldReduceMotion ? 0 : 0.2,
      ease: "easeOut"
    }} className="flex flex-col gap-2.5">{added.map(_temp12)}</m.div> : null;
    $[5] = added;
    $[6] = shouldReduceMotion;
    $[7] = visible;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== more || $[10] !== setVisible || $[11] !== total) {
    t5 = more > 0 ? <div className="mt-1 flex justify-start"><Button size="sm" variant="outline" className="text-[12px]" onClick={() => setVisible(total)}><IconDotsVertical className="!size-3.5" aria-hidden={true} /><span>Show {more} more</span></Button></div> : null;
    $[9] = more;
    $[10] = setVisible;
    $[11] = total;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = <div className="flex flex-col gap-2.5">{t3}{t4}{t5}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t2 || $[18] !== t6) {
    t7 = <div className="space-y-3">{t2}{t6}</div>;
    $[17] = t2;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function _temp12(u_0) {
  return <SitemapLink key={`sm-add-${u_0}`} url={u_0} />;
}
function _temp11(u) {
  return <SitemapLink key={`sm-ex-${u}`} url={u} />;
}